import React, { useState, useRef } from 'react';
import { FaSearch, FaTimes, FaUsers, FaUser, FaBell, FaStore } from 'react-icons/fa';
import { supabase } from '../services/supabase';
import { useAuth } from '../context/AuthContext';


// {kafe} placeholder-lı şablonlar — kafe seçiləndə avtomatik əvəzlənir
const CAFE_TEMPLATES = [
  { icon: '☕', label: 'Dostunla get',    title: 'Dostunla {kafe}-yə get!',         body: '{kafe}-də dostuna kofe ısmarla, birlikdə unudulmaz vaxt keçirin ☕🤝' },
  { icon: '🔥', label: 'Xüsusi endirim', title: '🔥 {kafe}-də bu həftə endirim!',   body: '{kafe}-ni ziyarət et, xüsusi endirimdən yararlan. Tez ol, vaxt məhduddur!' },
  { icon: '⭐', label: 'Kəşf et',         title: '{kafe}-ni kəşf et!',              body: '{kafe} səni gözləyir. Cafegoo ilə indi ziyarət et, xal toplayın 🗺️' },
  { icon: '🎯', label: 'Xal qazanç',      title: '{kafe}-dən xal topla!',           body: '{kafe}-yə hər ziyarətdə xal toplayın. Yetərli xalla pulsuz içki qazanın 🏆' },
  { icon: '🎁', label: 'Kampaniya',        title: '{kafe}-dən xüsusi kampaniya!',    body: '{kafe} tərəfdaş kafesindən bu həftəlik kampaniyadan yararlanın 🎁' },
];

const GENERAL_TEMPLATES = [
  { icon: '🎯', label: 'Xal xatırlatması',  title: 'Xal toplamağı unutmayın!',       body: 'Cafegoo ilə hər ziyarətdə xal toplayın. Yetərli xalla dostunuza pulsuz kofe alın ☕' },
  { icon: '🎁', label: 'Pulsuz kofe',        title: 'Pulsuz kofe qazanın!',            body: 'Kifayət qədər xal topladınızsa, dostunuza pulsuz kofe sifariş edin 🎉' },
  { icon: '🔥', label: 'Xüsusi kampaniya',   title: '🔥 Bu həftə xüsusi endirimlər!', body: 'Cafegoo tərəfdaş kafelərindən bu həftə xüsusi endirimlərdən yararlanın.' },
  { icon: '⭐', label: 'Yeni kafe',           title: 'Yeni kafe əlavə edildi!',         body: 'Sizin şəhərinizdə yeni kafe Cafegoo-da! İndi kəşf edin 🗺️' },
  { icon: '🏆', label: 'Liderlik',            title: 'Xal liderlik cədvəli!',           body: 'Bu ayın ən çox xal toplayan müştərilərini görün. Siz neçəncisiniz? 🏆' },
];

const CARD = { background: '#fff', borderRadius: 16, padding: '20px 24px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', marginBottom: 16 };

function Avatar({ name }) {
  const letter = (name || '?')[0].toUpperCase();
  const colors = ['#FF6B35','#6366F1','#10B981','#F59E0B','#EF4444','#8B5CF6'];
  const bg = colors[letter.charCodeAt(0) % colors.length];
  return (
    <div style={{ width: 36, height: 36, borderRadius: '50%', background: bg, color: '#fff',
      display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 15, flexShrink: 0 }}>
      {letter}
    </div>
  );
}

// {kafe} placeholder-ını əvəzlə
function fillTemplate(text, cafeName) {
  if (!cafeName) return text;
  return text.replace(/\{kafe\}/g, cafeName);
}

export default function NotificationsPage() {
  const { getAdminToken } = useAuth();
  // Target mode: 'all' | 'selected' | 'cafe'
  const [targetMode, setTargetMode] = useState('all');

  // User search (selected mode)
  const [userSearch,    setUserSearch]    = useState('');
  const [userResults,   setUserResults]   = useState([]);
  const [searching,     setSearching]     = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const userSearchTimer = useRef(null);

  // Cafe search (cafe mode)
  const [cafeSearch,    setCafeSearch]    = useState('');
  const [cafeResults,   setCafeResults]   = useState([]);
  const [cafeSearching, setCafeSearching] = useState(false);
  const [selectedCafe,  setSelectedCafe]  = useState(null);  // { id, name }
  const [cafeAudience,  setCafeAudience]  = useState('all'); // 'all' | 'followers'
  const cafeSearchTimer = useRef(null);

  // Compose
  const [pushTitle,   setPushTitle]   = useState('');
  const [pushBody,    setPushBody]    = useState('');
  const [pushChannel, setPushChannel] = useState('campaigns');

  // Send
  const [pushSending, setPushSending] = useState(false);
  const [pushResult,  setPushResult]  = useState(null);
  const [tokenCount,  setTokenCount]  = useState(null);

  const selectedSet = new Set(selectedUsers.map(u => u.id));

  // aktiv şablonlar + preview üçün kafe adı
  const activeTemplates = targetMode === 'cafe' ? CAFE_TEMPLATES : GENERAL_TEMPLATES;
  const cafeName = selectedCafe?.name || '';

  const previewTitle = fillTemplate(pushTitle, cafeName);
  const previewBody  = fillTemplate(pushBody,  cafeName);

  // ── User search ──────────────────────────────────────────────────────────────
  const handleUserSearch = (val) => {
    setUserSearch(val);
    clearTimeout(userSearchTimer.current);
    if (!val.trim() || val.trim().length < 2) { setUserResults([]); return; }
    userSearchTimer.current = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await supabase.rpc('admin_search_users', {
          p_admin_token: getAdminToken(), p_query: val.trim(), p_exclude_business: true, p_limit: 25,
        });
        setUserResults(data?.success ? data.rows : []);
      } catch { setUserResults([]); }
      finally { setSearching(false); }
    }, 300);
  };

  const toggleUser = (user) => {
    setSelectedUsers(prev =>
      prev.find(u => u.id === user.id)
        ? prev.filter(u => u.id !== user.id)
        : [...prev, { id: user.id, full_name: user.full_name, email: user.email }]
    );
  };

  // ── Cafe search ──────────────────────────────────────────────────────────────
  const handleCafeSearch = (val) => {
    setCafeSearch(val);
    setSelectedCafe(null);
    clearTimeout(cafeSearchTimer.current);
    if (!val.trim() || val.trim().length < 2) { setCafeResults([]); return; }
    cafeSearchTimer.current = setTimeout(async () => {
      setCafeSearching(true);
      try {
        const { data } = await supabase
          .from('businesses')
          .select('id, name, city')
          .ilike('name', `%${val.trim()}%`)
          .limit(20);
        setCafeResults(data || []);
      } catch { setCafeResults([]); }
      finally { setCafeSearching(false); }
    }, 300);
  };

  const selectCafe = (cafe) => {
    setSelectedCafe(cafe);
    setCafeSearch(cafe.name);
    setCafeResults([]);
    // Şablonun içindəki {kafe} placeholder-ını doldur
    if (pushTitle) setPushTitle(t => fillTemplate(t.replace(cafeName, '{kafe}'), cafe.name));
    if (pushBody)  setPushBody(b  => fillTemplate(b.replace(cafeName, '{kafe}'), cafe.name));
  };

  // ── Send ──────────────────────────────────────────────────────────────────────
  const sendNotification = async () => {
    if (!pushTitle.trim() || !pushBody.trim()) return;
    if (targetMode === 'selected' && selectedUsers.length === 0) return;
    if (targetMode === 'cafe' && !selectedCafe) return;
    setPushSending(true);
    setPushResult(null);
    try {
      let userIds = null; // null = hamıya (broadcast_push üçün)

      if (targetMode === 'selected') {
        userIds = selectedUsers.map(u => u.id);
      } else if (targetMode === 'cafe' && cafeAudience === 'followers') {
        const { data: followData } = await supabase.rpc('admin_get_business_followers', {
          p_admin_token: getAdminToken(), p_business_id: selectedCafe.id,
        });
        const follows = followData?.success ? followData.rows : [];
        userIds = [...new Set((follows || []).map(f => f.user_id))];
        if (userIds.length === 0) {
          setPushResult({ total: 0 });
          return;
        }
      }

      const finalTitle = fillTemplate(pushTitle, cafeName);
      const finalBody  = fillTemplate(pushBody,  cafeName);

      const { data, error } = await supabase.rpc('broadcast_push', {
        p_title:    finalTitle,
        p_body:     finalBody,
        p_channel:  pushChannel,
        p_user_ids: userIds,
      });
      if (error) throw new Error(error.message);
      const total = data?.total ?? 0;
      setTokenCount(total);
      setPushResult({ total });
    } catch (err) {
      setPushResult({ error: err.message });
    } finally {
      setPushSending(false);
    }
  };

  const canSend = !pushSending && pushTitle.trim() && pushBody.trim()
    && (targetMode === 'all'
      || (targetMode === 'selected' && selectedUsers.length > 0)
      || (targetMode === 'cafe' && selectedCafe));

  const resetMode = (mode) => {
    setTargetMode(mode);
    setPushResult(null);
    setPushTitle('');
    setPushBody('');
    if (mode !== 'cafe') { setSelectedCafe(null); setCafeSearch(''); }
    if (mode !== 'selected') setSelectedUsers([]);
  };

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div style={{ padding: 24, fontFamily: 'sans-serif', maxWidth: 900, margin: '0 auto' }}>

      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 800, color: '#1a1a2e', margin: '0 0 4px' }}>
          🔔 Push Bildirişlər
        </h1>
        <p style={{ color: '#888', fontSize: 13, margin: 0 }}>
          Bütün istifadəçilərə, seçilmiş şəxslərə və ya xüsusi kafe auditoriyasına bildiriş göndərin.
          {tokenCount !== null && (
            <span style={{ fontWeight: 700, color: '#FF6B35' }}> ({tokenCount} cihaz hədəfləndi)</span>
          )}
        </p>
      </div>

      {/* ── Target Mode ── */}
      <div style={CARD}>
        <h3 style={{ margin: '0 0 14px', fontSize: 15, fontWeight: 700, color: '#1a1a2e' }}>
          Hədəf Auditoriya
        </h3>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {[
            { k: 'all',      icon: <FaUsers />,  label: 'Hamıya göndər' },
            { k: 'selected', icon: <FaUser />,   label: 'Seçilmiş istifadəçilər' },
            { k: 'cafe',     icon: <FaStore />,  label: 'Kafeyə görə' },
          ].map(opt => (
            <button key={opt.k} onClick={() => resetMode(opt.k)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '10px 20px', borderRadius: 12, border: 'none', cursor: 'pointer',
                fontWeight: 700, fontSize: 14, transition: 'all 0.15s',
                background: targetMode === opt.k
                  ? (opt.k === 'cafe' ? '#10B981' : '#FF6B35')
                  : '#F1F5F9',
                color:     targetMode === opt.k ? '#fff' : '#64748B',
                boxShadow: targetMode === opt.k
                  ? `0 2px 12px ${opt.k === 'cafe' ? 'rgba(16,185,129,0.3)' : 'rgba(255,107,53,0.3)'}`
                  : 'none',
              }}>
              {opt.icon} {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── User Selector ── */}
      {targetMode === 'selected' && (
        <div style={CARD}>
          <h3 style={{ margin: '0 0 14px', fontSize: 15, fontWeight: 700, color: '#1a1a2e' }}>
            İstifadəçi Seç
            {selectedUsers.length > 0 && (
              <span style={{ marginLeft: 10, background: '#FF6B35', color: '#fff',
                borderRadius: 20, padding: '2px 10px', fontSize: 12 }}>
                {selectedUsers.length} seçilib
              </span>
            )}
          </h3>

          {selectedUsers.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
              {selectedUsers.map(u => (
                <div key={u.id} style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  background: '#FFF0EB', border: '1.5px solid #FF6B35',
                  borderRadius: 20, padding: '4px 8px 4px 6px', fontSize: 13,
                }}>
                  <Avatar name={u.full_name} />
                  <span style={{ fontWeight: 600, color: '#1a1a2e' }}>{u.full_name}</span>
                  <button onClick={() => toggleUser(u)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer',
                      color: '#FF6B35', padding: 0, display: 'flex', alignItems: 'center' }}>
                    <FaTimes size={11} />
                  </button>
                </div>
              ))}
              <button onClick={() => setSelectedUsers([])}
                style={{ background: 'none', border: '1.5px solid #E5E7EB', borderRadius: 20,
                  padding: '4px 12px', fontSize: 12, color: '#94A3B8', cursor: 'pointer', fontWeight: 600 }}>
                Hamısını sil
              </button>
            </div>
          )}

          <div style={{ position: 'relative', marginBottom: 12 }}>
            <FaSearch style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#94A3B8', pointerEvents: 'none' }} />
            <input value={userSearch} onChange={e => handleUserSearch(e.target.value)}
              placeholder="Ad, email və ya telefon ilə axtar..."
              style={{ width: '100%', padding: '10px 14px 10px 36px', borderRadius: 10,
                border: '1.5px solid #E5E7EB', fontSize: 14, outline: 'none',
                boxSizing: 'border-box', background: '#F8FAFC' }} />
            {searching && <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 12, color: '#94A3B8' }}>Axtarılır...</span>}
          </div>

          {userResults.length > 0 && (
            <div style={{ border: '1.5px solid #E5E7EB', borderRadius: 10, overflow: 'hidden' }}>
              {userResults.map((u, i) => {
                const isSel = selectedSet.has(u.id);
                return (
                  <div key={u.id} onClick={() => toggleUser(u)}
                    style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px',
                      cursor: 'pointer', background: isSel ? '#FFF0EB' : i % 2 === 0 ? '#fff' : '#FAFAFA',
                      borderBottom: i < userResults.length - 1 ? '1px solid #F1F5F9' : 'none' }}>
                    <div style={{ width: 20, height: 20, borderRadius: 6, flexShrink: 0,
                      border: `2px solid ${isSel ? '#FF6B35' : '#D1D5DB'}`,
                      background: isSel ? '#FF6B35' : '#fff',
                      display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {isSel && <span style={{ color: '#fff', fontSize: 11, fontWeight: 900 }}>✓</span>}
                    </div>
                    <Avatar name={u.full_name} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: 14, color: '#1a1a2e', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{u.full_name || '—'}</div>
                      <div style={{ fontSize: 12, color: '#64748B', marginTop: 1 }}>{u.email || u.phone || '—'}</div>
                    </div>
                    <div style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10,
                      background: u.user_type === 'visitor' ? '#EFF6FF' : '#F0FDF4',
                      color:      u.user_type === 'visitor' ? '#3B82F6' : '#10B981' }}>
                      {u.user_type === 'visitor' ? 'Turist' : 'Yerli'}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {!searching && userSearch.length >= 2 && userResults.length === 0 && (
            <div style={{ textAlign: 'center', padding: '20px', color: '#94A3B8', fontSize: 13 }}>Nəticə tapılmadı</div>
          )}
          {userSearch.length < 2 && selectedUsers.length === 0 && (
            <div style={{ textAlign: 'center', padding: '16px', color: '#CBD5E1', fontSize: 13 }}>Ən azı 2 hərf yazın</div>
          )}
        </div>
      )}

      {/* ── Cafe Selector ── */}
      {targetMode === 'cafe' && (
        <div style={CARD}>
          <h3 style={{ margin: '0 0 14px', fontSize: 15, fontWeight: 700, color: '#1a1a2e' }}>
            Kafe Seç
          </h3>

          {/* Cafe search */}
          <div style={{ position: 'relative', marginBottom: 14 }}>
            <FaStore style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#10B981', pointerEvents: 'none' }} />
            <input value={cafeSearch} onChange={e => handleCafeSearch(e.target.value)}
              placeholder="Kafenin adını yazın..."
              style={{ width: '100%', padding: '10px 14px 10px 36px', borderRadius: 10,
                border: `1.5px solid ${selectedCafe ? '#10B981' : '#E5E7EB'}`,
                fontSize: 14, outline: 'none', boxSizing: 'border-box', background: '#F8FAFC' }} />
            {cafeSearching && <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 12, color: '#94A3B8' }}>Axtarılır...</span>}
            {selectedCafe && (
              <button onClick={() => { setSelectedCafe(null); setCafeSearch(''); setPushTitle(''); setPushBody(''); }}
                style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                  background: 'none', border: 'none', cursor: 'pointer', color: '#94A3B8' }}>
                <FaTimes size={13} />
              </button>
            )}
          </div>

          {/* Results dropdown */}
          {cafeResults.length > 0 && (
            <div style={{ border: '1.5px solid #E5E7EB', borderRadius: 10, overflow: 'hidden', marginBottom: 14 }}>
              {cafeResults.map((c, i) => (
                <div key={c.id} onClick={() => selectCafe(c)}
                  style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px',
                    cursor: 'pointer', background: i % 2 === 0 ? '#fff' : '#FAFAFA',
                    borderBottom: i < cafeResults.length - 1 ? '1px solid #F1F5F9' : 'none',
                    transition: 'background 0.1s' }}>
                  <div style={{ width: 36, height: 36, borderRadius: 10, background: '#ECFDF5',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>☕</div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14, color: '#1a1a2e' }}>{c.name}</div>
                    {c.city && <div style={{ fontSize: 12, color: '#64748B' }}>{c.city}</div>}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Selected cafe chip */}
          {selectedCafe && (
            <div style={{ background: '#ECFDF5', border: '1.5px solid #10B981', borderRadius: 12,
              padding: '12px 16px', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 22 }}>☕</span>
              <div>
                <div style={{ fontWeight: 800, fontSize: 15, color: '#065F46' }}>{selectedCafe.name}</div>
                <div style={{ fontSize: 12, color: '#10B981' }}>Seçilmiş kafe — şablonlarda <code style={{ background: '#D1FAE5', padding: '1px 5px', borderRadius: 4 }}>{'{kafe}'}</code> bu adla əvəzlənir</div>
              </div>
            </div>
          )}

          {/* Audience toggle */}
          <div>
            <label style={{ fontSize: 12, fontWeight: 700, color: '#475569', display: 'block', marginBottom: 8 }}>
              Kimlərə göndərilsin?
            </label>
            <div style={{ display: 'flex', gap: 8 }}>
              {[
                { k: 'all',       label: '🌍 Bütün istifadəçilər' },
                { k: 'followers', label: '❤️ Kafenin izləyiciləri' },
              ].map(opt => (
                <button key={opt.k} onClick={() => setCafeAudience(opt.k)}
                  style={{ padding: '8px 16px', borderRadius: 10, border: 'none', cursor: 'pointer',
                    fontWeight: 700, fontSize: 13, transition: 'all 0.15s',
                    background: cafeAudience === opt.k ? '#10B981' : '#F1F5F9',
                    color:      cafeAudience === opt.k ? '#fff'    : '#64748B' }}>
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Templates ── */}
      <div style={CARD}>
        <h3 style={{ margin: '0 0 14px', fontSize: 15, fontWeight: 700, color: '#1a1a2e' }}>
          Şablon Seçin
          {targetMode === 'cafe' && (
            <span style={{ marginLeft: 10, fontSize: 12, fontWeight: 500, color: '#10B981' }}>
              — kafe şablonları
            </span>
          )}
        </h3>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          {activeTemplates.map((t, i) => {
            const tTitle = fillTemplate(t.title, cafeName);
            const isActive = pushTitle === tTitle || pushTitle === t.title;
            return (
              <button key={i}
                onClick={() => { setPushTitle(fillTemplate(t.title, cafeName)); setPushBody(fillTemplate(t.body, cafeName)); }}
                style={{ padding: '8px 16px', borderRadius: 20, cursor: 'pointer', fontSize: 13, fontWeight: 600,
                  border: isActive ? 'none' : '1.5px solid #E5E7EB',
                  background: isActive ? (targetMode === 'cafe' ? '#10B981' : '#FF6B35') : '#F8FAFC',
                  color:      isActive ? '#fff' : '#374151', transition: 'all 0.15s' }}>
                {t.icon} {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Compose ── */}
      <div style={CARD}>
        <h3 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 700, color: '#1a1a2e' }}>Bildiriş Yaz</h3>

        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 12, color: '#888', fontWeight: 600, display: 'block', marginBottom: 4 }}>Başlıq</label>
          <input value={pushTitle} onChange={e => setPushTitle(e.target.value)}
            placeholder={targetMode === 'cafe' ? 'Dostunla {kafe}-yə get! (kafe adı avtomatik əvəzlənir)' : 'Bildiriş başlığı...'}
            style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: '1.5px solid #E5E7EB',
              fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 12, color: '#888', fontWeight: 600, display: 'block', marginBottom: 4 }}>Mətn</label>
          <textarea value={pushBody} onChange={e => setPushBody(e.target.value)}
            placeholder={targetMode === 'cafe' ? '{kafe}-də dostuna kofe ısmarla...' : 'Bildiriş mətni...'}
            rows={3}
            style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: '1.5px solid #E5E7EB',
              fontSize: 14, outline: 'none', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'sans-serif' }} />
        </div>

        <div style={{ marginBottom: 20 }}>
          <label style={{ fontSize: 12, color: '#888', fontWeight: 600, display: 'block', marginBottom: 4 }}>Kanal</label>
          <select value={pushChannel} onChange={e => setPushChannel(e.target.value)}
            style={{ padding: '10px 14px', borderRadius: 10, border: '1.5px solid #E5E7EB', fontSize: 14, outline: 'none', cursor: 'pointer' }}>
            <option value="campaigns">Kampaniyalar</option>
            <option value="reminders">Xatırlatmalar</option>
            <option value="general">Ümumi</option>
          </select>
        </div>

        {/* Preview */}
        {(previewTitle || previewBody) && (
          <div style={{ background: '#F8FAFC', borderRadius: 12, padding: '14px 16px',
            border: '1.5px solid #E5E7EB', marginBottom: 20 }}>
            <div style={{ fontSize: 11, color: '#94A3B8', fontWeight: 600, marginBottom: 8,
              display: 'flex', alignItems: 'center', gap: 5 }}>
              <FaBell size={10} /> Önizləmə
              {targetMode === 'cafe' && selectedCafe && (
                <span style={{ color: '#10B981', marginLeft: 4 }}>— «{selectedCafe.name}» ilə</span>
              )}
            </div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
              <div style={{ width: 36, height: 36, borderRadius: 8, background: '#E63950',
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>☕</div>
              <div>
                <div style={{ fontWeight: 700, fontSize: 13, color: '#1a1a2e' }}>{previewTitle || '—'}</div>
                <div style={{ fontSize: 12, color: '#64748B', marginTop: 2, lineHeight: 1.4 }}>{previewBody || '—'}</div>
              </div>
            </div>
          </div>
        )}

        {/* Send Button */}
        <button onClick={sendNotification} disabled={!canSend}
          style={{ padding: '13px 32px', borderRadius: 12, border: 'none', cursor: canSend ? 'pointer' : 'not-allowed',
            fontWeight: 700, fontSize: 15, transition: 'all 0.15s',
            background: canSend
              ? (targetMode === 'cafe' ? '#10B981' : '#FF6B35')
              : '#E5E7EB',
            color: canSend ? '#fff' : '#94A3B8',
            boxShadow: canSend
              ? `0 4px 16px ${targetMode === 'cafe' ? 'rgba(16,185,129,0.35)' : 'rgba(255,107,53,0.35)'}`
              : 'none' }}>
          {pushSending ? '⏳ Göndərilir...'
            : targetMode === 'all'      ? '🚀 Hamıya Göndər'
            : targetMode === 'selected' ? `🚀 ${selectedUsers.length} Nəfərə Göndər`
            : cafeAudience === 'followers'
              ? `🚀 ${selectedCafe?.name || 'Kafe'} İzləyicilərinə Göndər`
              : `🚀 Hamıya Göndər — ${selectedCafe?.name || 'Kafe'} Mövzusunda`
          }
        </button>
      </div>

      {/* ── Result ── */}
      {pushResult && (
        <div style={{ borderRadius: 14, padding: '16px 20px', fontWeight: 600, fontSize: 14,
          background: pushResult.error ? '#FEF2F2' : pushResult.total === 0 ? '#FFFBEB' : '#F0FDF4',
          color:      pushResult.error ? '#EF4444' : pushResult.total === 0 ? '#D97706' : '#16A34A',
          border: `1.5px solid ${pushResult.error ? '#FECACA' : pushResult.total === 0 ? '#FDE68A' : '#BBF7D0'}` }}>
          {pushResult.error
            ? '❌ Xəta: ' + pushResult.error
            : pushResult.total === 0
              ? '⚠️ Bu auditoriyada qeydli cihaz tapılmadı'
              : `✅ ${pushResult.total} cihaza göndərildi`
          }
        </div>
      )}
    </div>
  );
}
